import { AlertTriangle, BookOpenCheck, CircleDashed, Clock, Copy, Eye, Loader2, MinusCircle, type LucideIcon } from 'lucide-react';
import type { FeedIngestion } from '../../lib/api';
import { cn } from '../../lib/utils';

/**
 * Where an arrival is on its way into the memory, as one coloured pill — the Feed's answer to "did
 * this get in yet?". The colours run cool to warm to green the way the item moves: waiting (amber),
 * picked up (sky), being written (orange, breathing), in memory (green). Read, nothing new and held
 * are greys of different weight, since each is finished and none is a problem. The full sentence is
 * the tooltip.
 */
const STATE_STYLES: Record<FeedIngestion['state'], { icon: LucideIcon; tone: string; spin?: boolean }> = {
  waiting: { icon: Clock, tone: 'bg-amber-100 text-amber-800 dark:bg-amber-500/20 dark:text-amber-300' },
  queued: { icon: CircleDashed, tone: 'bg-sky-100 text-sky-800 dark:bg-sky-500/20 dark:text-sky-300' },
  processing: { icon: Loader2, tone: 'bg-orange-100 text-orange-800 dark:bg-orange-500/20 dark:text-orange-300', spin: true },
  in_memory: { icon: BookOpenCheck, tone: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-500/20 dark:text-emerald-300' },
  read: { icon: Eye, tone: 'bg-slate-100 text-slate-700 dark:bg-slate-500/20 dark:text-slate-300' },
  nothing_new: { icon: MinusCircle, tone: 'bg-zinc-100 text-zinc-600 dark:bg-zinc-500/20 dark:text-zinc-400' },
  held: { icon: Copy, tone: 'bg-zinc-100 text-zinc-500 dark:bg-zinc-500/15 dark:text-zinc-400' },
  failed: { icon: AlertTriangle, tone: 'bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-300' },
};

/** Still on its way — what the Feed keeps polling. */
export function isMoving(ingestion: FeedIngestion | undefined): boolean {
  return !!ingestion && (ingestion.state === 'waiting' || ingestion.state === 'queued' || ingestion.state === 'processing');
}

export function IngestionPill({ ingestion, className }: { ingestion: FeedIngestion | undefined; className?: string }) {
  if (!ingestion) return null;
  const style = STATE_STYLES[ingestion.state];
  const Icon = style.icon;
  return (
    <span
      title={ingestion.detail}
      className={cn(
        'inline-flex flex-shrink-0 items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-medium whitespace-nowrap',
        style.tone,
        className,
      )}
    >
      <Icon size={10} strokeWidth={2.25} className={style.spin ? 'animate-spin' : undefined} />
      {ingestion.label}
    </span>
  );
}
